import React from 'react';
import { Box, Checkbox, FormControlLabel, TextField, Typography, Paper, Tooltip, Select, MenuItem, FormLabel } from '@mui/material';
import InfoTooltip from '../../InfoTooltip';

interface PatchContentProps {
  patchOptions: PatchOptionsState;
  setPatchOptions: (opts: PatchOptionsState) => void;
  patchFolder: string;
  setPatchFolder: (v: string) => void;
  patchFolderPlaceholder: string;
  executionMode?: string;
  targetVersionEnabled?: boolean;
  targetVersion?: string;
  platform: string;
  download?: 'NO' | 'YES';
}

export interface PatchOptionsState {
  recommended: boolean;
  ru: boolean;
  ruVersion: string;
  ojvm: boolean;
  ojvmVersion: string;
  opatch: boolean;
  dpbp: boolean;
  au?: boolean;
  jdk: boolean;
  specificPatches: string;
  mrp?: boolean;
  ocw?: boolean;
  ocwVersion: string;
  sdo?: boolean;
  text?: boolean;
  sourceHome?: boolean;
  goldImage?: 'NO' | 'YES' | 'ALL' | 'AUTO';
}

const PatchContent: React.FC<PatchContentProps & { patchKey?: string }> = ({ patchOptions, setPatchOptions, patchFolder, setPatchFolder, patchFolderPlaceholder, executionMode, targetVersionEnabled, targetVersion, patchKey, platform, download }) => {
  const [specificPatchesTouched, setSpecificPatchesTouched] = React.useState(false);
  const [patchFolderTouched, setPatchFolderTouched] = React.useState(false);

  const handleChange = (field: keyof PatchOptionsState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPatchOptions({ ...patchOptions, [field]: e.target.checked });
  };
  const handleInput = (field: keyof PatchOptionsState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPatchOptions({ ...patchOptions, [field]: e.target.value });
  };
  const allDisabled = patchOptions.recommended;

  const isDownloadOrCreateHome = executionMode === 'download' || executionMode === 'create_home';
  // Suggest version placeholder based on selected target_version across all modes
  const optionalVersionPlaceholder = (() => {
    if (!targetVersionEnabled) return '19.30';
    const v = String(targetVersion ?? '19').trim();
    if (v === '19') return '19.30';
    if (v === '21') return '21.20';
    if (v === '23' || v === '26') return '23.26.1';
    return '19.30';
  })();
  const ojvmEmbedded = !!targetVersionEnabled && (targetVersion === '21' || targetVersion === '23' || targetVersion === '26');
  const sdoDisabled = !!targetVersionEnabled && (targetVersion === '21' || targetVersion === '23' || targetVersion === '26');
  const textDisabled = !!targetVersionEnabled && (targetVersion === '21' || targetVersion === '23' || targetVersion === '26');
  // Validation for RU/OJVM version in Download or Create Home Mode
  const ruVersionInvalid = patchOptions.ru && isDownloadOrCreateHome && patchOptions.ruVersion && !/^(19|21|23|26)(\.|$)/.test(patchOptions.ruVersion);
  const ojvmVersionInvalid = patchOptions.ojvm && isDownloadOrCreateHome && patchOptions.ojvmVersion && !/^(19|21|23|26)(\.|$)/.test(patchOptions.ojvmVersion);

  // Improved Validation for MRP + RU version < 19.17 (all modes)
  let mrpVersionWarning = false;
  let ruMinor = null;
  if (patchOptions.ru && patchOptions.ruVersion) {
    const match = patchOptions.ruVersion.match(/^19\.(\d+)/);
    if (match) {
      ruMinor = Number(match[1]);
    }
  }
  if (patchOptions.mrp && ruMinor !== null && ruMinor < 17) {
    mrpVersionWarning = true;
  }

  // Determine if MRP should be shown
  const showMRP =
    (executionMode === 'download' && platform === 'LINUX.X64') ||
    (['deploy', 'create_home', 'fixups', 'analyze'].includes(executionMode || '') && platform !== 'WINDOWS.X64');

  const showRuTooltip = (isDownloadOrCreateHome && !patchOptions.sourceHome && !targetVersionEnabled);

  return (
    <Paper elevation={0} sx={{ p: 3, background: 'background.paper', borderRadius: 3 }}>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={patchOptions.recommended} onChange={handleChange('recommended')} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>RECOMMENDED</Typography>
              <InfoTooltip
                title={
                  <>AutoUpgrade will consider all of the following options: <b>RU, OPATCH, OJVM, DPBP</b>.</>
                }
                placement="top"
              />
            </Box>
          }
        />
      </Box>
      <Box sx={{ mb: 2, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            minWidth: 0,
            background: theme => theme.palette.mode === 'dark' ? '#333' : '#e3f2fd',
            color: theme => theme.palette.mode === 'dark' ? '#b2ebf2' : '#1976d2',
            borderRadius: 2,
            px: 2,
            py: 1,
            my: 2,
            fontWeight: 500,
            fontSize: 15,
            maxWidth: '100%',
            gap: 1.5,
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              flex: 1,
            }}
          >
            If no version is specified below, the latest available will be used automatically.
          </span>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, columnGap: 4, alignItems: 'flex-start' }}>
        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200, flex: 1 }}>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={patchOptions.recommended || patchOptions.ru} onChange={handleChange('ru')} disabled={allDisabled} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', ...(allDisabled && { color: 'text.disabled' }) }}>
              {showRuTooltip ? (
                <Tooltip
                  title="If Source Home or Target Version are not selected, specify a RU using RU:x.y notation where AutoUpgrade patching can derive the target version."
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                        fontSize: 15,
                        borderRadius: 2,
                        boxShadow: 3,
                        px: 2,
                        py: 1,
                        fontWeight: 400,
                        minWidth: 320,
                      },
                    },
                  }}
                >
                  <span>
                    <Typography sx={{ fontWeight: 600, mr: 1, display: 'inline' }}>RU</Typography>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip
                  title="Database Release Update"
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                        fontSize: 15,
                        borderRadius: 2,
                        boxShadow: 3,
                        px: 2,
                        py: 1,
                        fontWeight: 400,
                        minWidth: 220,
                      },
                    },
                  }}
                >
                  <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>RU</Typography>
                </Tooltip>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isDownloadOrCreateHome ? (
                  <Tooltip
                    title="This may be required if Source Home or Target Version are not specified."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder={`Optional: ${optionalVersionPlaceholder}`}
                      value={patchOptions.ruVersion}
                      onChange={handleInput('ruVersion')}
                      disabled={allDisabled}
                      sx={{ width: 160 }}
                      error={!!ruVersionInvalid}
                    />
                  </Tooltip>
                ) : (
                  <TextField
                    size="small"
                    placeholder={`Optional: ${optionalVersionPlaceholder}`}
                    value={patchOptions.ruVersion}
                    onChange={handleInput('ruVersion')}
                    disabled={allDisabled}
                    sx={{ width: 160 }}
                    error={!!ruVersionInvalid}
                  />
                )}
                {ruVersionInvalid && (
                  <Typography sx={{ color: 'error.main', fontSize: 14, whiteSpace: 'nowrap' }}>
                    Only 19.x/21.x/23.x/26.x versions are allowed in Create Home and Download Modes
                  </Typography>
                )}
              </Box>
            </Box>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={patchOptions.recommended || patchOptions.ojvm} onChange={handleChange('ojvm')} disabled={allDisabled || ojvmEmbedded} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', ...((allDisabled || ojvmEmbedded) && { color: 'text.disabled' }) }}>
              <Tooltip
                title="Oracle JavaVM Component Release Update"
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 220,
                    },
                  },
                }}
              >
                <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>OJVM</Typography>
              </Tooltip>
              {ojvmEmbedded ? (
                <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>
                  OJVM is already included in Release Update
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    size="small"
                    placeholder={`Optional: ${optionalVersionPlaceholder}`}
                    value={patchOptions.ojvmVersion}
                    onChange={handleInput('ojvmVersion')}
                    disabled={allDisabled}
                    sx={{ width: 160 }}
                    error={!!ojvmVersionInvalid}
                  />
                  {ojvmVersionInvalid && (
                    <Typography sx={{ color: 'error.main', fontSize: 14, whiteSpace: 'nowrap' }}>
                      Only 19.x/21.x/23.x/26.x versions are allowed in Create Home and Download Modes
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={patchOptions.recommended || patchOptions.opatch} onChange={handleChange('opatch')} disabled={allDisabled} />}
          label={
            <Tooltip
              title="Oracle Patch Utility"
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 220,
                  },
                },
              }}
            >
              <Typography component="span" sx={{ fontWeight: 600, ...(allDisabled && { color: 'text.disabled' }) }}>OPATCH</Typography>
            </Tooltip>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={patchOptions.recommended || patchOptions.dpbp} onChange={handleChange('dpbp')} disabled={allDisabled} />}
          label={
            <Tooltip
              title="Data Pump Bundle Patch"
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 220,
                  },
                },
              }}
            >
              <Typography component="span" sx={{ fontWeight: 600, ...(allDisabled && { color: 'text.disabled' }) }}>DPBP</Typography>
            </Tooltip>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={patchOptions.recommended || !!patchOptions.au} onChange={handleChange('au')} disabled={allDisabled} />}
          label={
            <Tooltip
              title="AutoUpgrade"
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 220,
                  },
                },
              }}
            >
              <Typography component="span" sx={{ fontWeight: 600, ...(allDisabled && { color: 'text.disabled' }) }}>AU</Typography>
            </Tooltip>
          }
        />
      </Box>
        </Box>
        {/* Right column - JDK first */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200, flex: 1 }}>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={patchOptions.jdk} onChange={handleChange('jdk')} />}
          label={
            <Tooltip
              title="Database Client JDK"
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 220,
                  },
                },
              }}
            >
              <Typography component="span" sx={{ fontWeight: 600 }}>JDK</Typography>
            </Tooltip>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={!!patchOptions.ocw} onChange={handleChange('ocw')} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip
                title="Grid Infrastructure Release Update"
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 220,
                    },
                  },
                }}
              >
                <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>OCW</Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  size="small"
                  placeholder={`Optional: ${optionalVersionPlaceholder}`}
                  value={patchOptions.ocwVersion}
                  onChange={handleInput('ocwVersion')}
                  disabled={!patchOptions.ocw}
                  sx={{ width: 160 }}
                />
              </Box>
            </Box>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={!!patchOptions.sdo} onChange={handleChange('sdo')} disabled={sdoDisabled} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', ...(sdoDisabled && { color: 'text.disabled' }) }}>
              <Tooltip
                title="Spatial Bundle Patch"
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 220,
                    },
                  },
                }}
              >
                <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>SDO</Typography>
              </Tooltip>
              {sdoDisabled && (
                <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>
                  Temporarily disabled for 21c/23c/26ai — It will be released on AutoUpgrade version 26.3
                </Typography>
              )}
            </Box>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={<Checkbox checked={!!patchOptions.text} onChange={handleChange('text')} disabled={textDisabled} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', ...(textDisabled && { color: 'text.disabled' }) }}>
              <Tooltip
                title="Oracle Text Patch"
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 220,
                    },
                  },
                }}
              >
                <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>TEXT</Typography>
              </Tooltip>
              {textDisabled && (
                <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>
                  Already included in Release Update
                </Typography>
              )}
            </Box>
          }
        />
      </Box>
      {showMRP && (
        <Box>
          <FormControlLabel
            control={<Checkbox checked={!!patchOptions.mrp} onChange={handleChange('mrp')} />}
            label={
              <Tooltip
                title="Monthly Recommended Patches"
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 220,
                    },
                  },
                }}
              >
                <Typography component="span" sx={{ fontWeight: 600 }}>MRP</Typography>
              </Tooltip>
            }
          />
        </Box>
      )}
        </Box>
      </Box>
      {mrpVersionWarning && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, ml: 1, border: '2px solid', borderColor: 'error.main', borderRadius: 2, p: 2, background: theme => theme.palette.mode === 'dark' ? '#2a2323' : '#fff0f0' }}>
          <Box sx={{ color: 'error.main', mt: '2px', mr: 1 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 20h20L12 2zm0 14v-4m0 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
          <Typography sx={{ color: 'error.main', fontSize: 15, fontWeight: 500 }}>
            The patch parameter for prefix <b>{patchKey || 'patch1'}</b> is invalid. Monthly Recommended Patches only exist for RU versions starting 19.17
          </Typography>
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Specific Patch(es):</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="35197819,<patch2>,<patch3>"
          value={patchOptions.specificPatches}
          onChange={e => { setSpecificPatchesTouched(true); handleInput('specificPatches')(e); }}
          onFocus={e => { if (!specificPatchesTouched && patchOptions.specificPatches === '') handleInput('specificPatches')(e); setSpecificPatchesTouched(true); }}
          InputProps={{
            sx: {
              '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color: !specificPatchesTouched && patchOptions.specificPatches === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
              background: 'transparent',
            },
          }}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Patch Folder:</Typography>
        <TextField
          fullWidth
          size="small"
          value={patchFolder}
          onChange={e => { setPatchFolderTouched(true); setPatchFolder(e.target.value); }}
          onFocus={() => { if (!patchFolderTouched && patchFolder === '') setPatchFolder(''); setPatchFolderTouched(true); }}
          placeholder={patchFolderPlaceholder}
          InputProps={{
            sx: {
              '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color: !patchFolderTouched && patchFolder === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
              background: 'transparent',
            },
          }}
        />
      </Box>
      {['download', 'deploy', 'analyze'].includes(executionMode ?? '') && (
        <Box sx={{ mt: 3 }}>
          {(executionMode === 'deploy' || executionMode === 'analyze') && download === 'NO' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                minWidth: 0,
                background: theme => theme.palette.mode === 'dark' ? '#333' : '#e3f2fd',
                color: theme => theme.palette.mode === 'dark' ? '#b2ebf2' : '#1976d2',
                borderRadius: 2,
                px: 2,
                py: 1,
                mb: 2,
                fontWeight: 500,
                fontSize: 15,
                maxWidth: '100%',
                gap: 1.5,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>
                To change Gold Image settings, enable Download in Download Settings tab
              </span>
            </Box>
          )}
          <Box sx={{ ...((executionMode === 'deploy' || executionMode === 'analyze') && download === 'NO' && { opacity: 0.6 }) }}>
          <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
            <Tooltip
              title={
                <Typography sx={{ fontSize: 16, lineHeight: 1.5 }}>
                  <Box sx={{ pt: 1 }}>
                    <Typography sx={{ mb: 1 }}>&nbsp;</Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>NO:</Typography>
                    <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                      Gold Image will not be used. This maintains the previous behavior where patches are individually downloaded from MOS and applied. Note: For releases prior to 26ai, the base release must be placed in the patches folder.
                    </Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>YES:</Typography>
                    <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                      The Gold Image feature is enabled. When this value is provided, AU will request and download the generated Gold Image for all patches from Oracle Updater Service, except for anything that is a number in the patch parameter (one-offs). So, the one-off patches are still downloaded and applied individually from MOS. This option will require that the MOS CSI be loaded into the keystore.
                    </Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>ALL:</Typography>
                    <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                      The Gold Image feature is enabled. Differently from YES, if ALL is provided, AU will download the generated Gold Image for all patches from Oracle Updater Service. This option will require that the MOS CSI be loaded into the keystore.
                    </Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>AUTO (default):</Typography>
                    <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                      The default value of AUTO will automatically decide the best approach, selecting between YES or NO. If all of these preconditions are met, then AUTO is resolved as YES. Otherwise, AUTO is resolved as NO:
                    </Typography>
                    <ul style={{ marginTop: 0, marginBottom: 12, paddingLeft: 20, fontSize: 15 }}>
                      <li>A valid CSI with My Oracle Support credentials is loaded into the AutoUpgrade Patching keystore</li>
                      <li>Connection to Oracle Updater is successful (if you are behind a firewall, https://transport.oracle.com/ needs to be whitelisted)</li>
                      <li>The requested platform is LINUX.X64</li>
                      <li>The requested RU is available on the Oracle Updater service (the service only provides the 3 latest RUs)</li>
                    </ul>
                    <Typography sx={{ mt: 1 }}>&nbsp;</Typography>
                  </Box>
                </Typography>
              }
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    borderRadius: 8,
                    fontSize: 16,
                    py: 2.2,
                    px: 4,
                    boxShadow: 3,
                    maxWidth: 1100,
                    minWidth: 220,
                  },
                },
                arrow: {
                  sx: {
                    color: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  },
                },
              }}
            >
              <Typography sx={{ fontWeight: 600, display: 'inline', cursor: 'help' }}>Gold Image:</Typography>
            </Tooltip>
          </FormLabel>
          <Select
            size="small"
            value={patchOptions.goldImage || 'AUTO'}
            onChange={e => setPatchOptions({ ...patchOptions, goldImage: e.target.value as 'NO' | 'YES' | 'ALL' | 'AUTO' })}
            disabled={(executionMode === 'deploy' || executionMode === 'analyze') && download === 'NO'}
            sx={{ width: 120 }}
          >
            <MenuItem value="NO">No</MenuItem>
            <MenuItem value="YES">Yes</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="AUTO">Auto</MenuItem>
          </Select>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default PatchContent; 